import type {
    ComposableOptionsBase,
    FullImageSizesList,
    FullImage
} from '~/types';
import { type ImageSizeTypes, imageSizeTypesForProduct } from '~/models';
import {
    useProductImageUtils,
    useFullImage
} from '~/composables';
import {
    fallbackImgUrlBlank_1x1
} from 'x1-base/constants';
import { castArray as lo_castArray } from 'es-toolkit/compat';

export interface UseFullImageSetupOptions extends ComposableOptionsBase {
    imageObject?: Nilish<FullImage>;
    type?: Nilish<ImageSizeTypes | ImageSizeTypes[]>;
    disableDropShadow?: boolean;
}
export function useFullImageSetup(options: Partial<UseFullImageSetupOptions> = {
    type: 'medium',
    disableDropShadow: false
}) {
    const {
        fallbackImage,
        getImgAttrsFromFullImage
    } = useFullImage();
    const {
        getFirstPreferredSize
    } = useProductImageUtils({
        fallbackImgUrl: fallbackImage
    });
    const altText = ref<string>('');
    const imgSrc = ref<string>('');
    const imgSrcSet = ref<string>('');
    const sizes = ref<string>('');
    const sizesMap = ref<Record<string, string>>({});
    const sizesList = ref<FullImageSizesList[]>([]);
    const widths = ref<number[]>([]);
    const mqSizes = ref<string[]>([]);
    const isNonImage = ref(!options.imageObject?.url);

    const disableDropShadowOverride = ref(options.disableDropShadow);

    function init(): void {
        const img = options.imageObject;

        if (!img) {
            return;
        }

        if (!img.url) {
            imgSrc.value = fallbackImgUrlBlank_1x1;
            return;
        }

        if (img.alt) {
            altText.value = img.alt;
        }

        const sizesObj = getImgAttrsFromFullImage(img, imageSizeTypesForProduct);

        if (!options.type) {
            sizes.value = sizesObj.sizes;
            widths.value = sizesObj.widths;
            mqSizes.value = sizesObj.mqSizes;
            sizesMap.value = sizesObj.sizesMap;
            sizesList.value = sizesObj.sizesList;
            imgSrcSet.value = sizesObj.srcset;
            imgSrc.value = getFirstPreferredSize(sizesObj, ['medium']);
            return;
        }

        imgSrc.value = getFirstPreferredSize(sizesObj, lo_castArray(options.type));
    }

    function onError(e: Nilish<Event | ErrorEvent | string>): void {
        disableDropShadowOverride.value = true;
        imgSrc.value = fallbackImage.value;
        imgSrcSet.value = '';
    }

    return {
        init,
        imgSrc,
        imgSrcSet,
        altText,
        sizes,
        sizesMap,
        sizesList,
        widths,
        mqSizes,
        disableDropShadowOverride,
        isNonImage,
        onError
    };
}
